import React, { FC, useState } from 'react'
import { LoginFormProps, LoginFormData } from './schema'
import { withFeatures } from 'features'
import { LoginPageContainer } from './loginContainer'
import { withNavigation } from 'hocs'
import TextInputFieldFormik from 'uiComponents/input/textInput/textInputFieldFormik'
import styled from 'styled-typed'
import { ActionButton } from 'uiComponents/buttons'
import { StyledATag, Body, Text } from 'uiComponents/typography'
import { LoginFormSchema } from './validation'
import { FormWrapper } from 'uiComponents/form/form'
import { useTranslation } from 'translations'

const LoginHeader = styled.h1`
    font-size: 2.625em;
    line-height: 3.5rem;
    font-weight: bold;
    margin: 0 0 0.5rem 0;
`
const LoginSubheader = styled(Body)`
    margin: 0 0 2rem 0;
    color: ${(props) => props.theme.colors.textLight};
`

const ForgotPasswordLinkWrapper = styled(Body)`
    margin-top: 0.7em;

    a {
        text-decoration: none;
    }
`
const ValidationMessage = styled(Text).attrs((props) => ({
    status: 'error',
}))`
    font-size: 0.75em;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s ease-in;
    display: block;
    margin-bottom: 0.3em;

    &.validation-message-visible {
        opacity: 1;
        visibility: visible;
    }
`
export const Separator = styled.div`
    color: ${(props) => props.theme.colors.textLight};
    font-size: 0.875em;
    margin: 2em 0 1em 0;
    position: relative;
    text-align: center;

    &:before,
    &:after {
        content: '';
        display: block;
        height: 2px;
        background: ${(props) => props.theme.colors.textLight};
        width: 40%;
        opacity: 0.5;
        position: absolute;
        top: 0.43em;
    }
    &:after {
        right: 0;
    }
`
const LoginButton = styled(ActionButton)`
    margin-bottom: 0.5em;
`

const EmailInput = styled(TextInputFieldFormik)`
    margin-bottom: 1em;
`

const PasswordInput = styled(TextInputFieldFormik)`
    margin-bottom: 2.5em;
`

const StyledFormWrapper = styled(FormWrapper)`
    width: 100%;
`

const initialFormValues: LoginFormData = {
    email: '',
    password: '',
}

export const LoginPage: FC<LoginFormProps> = (props) => {
    const { navigation, login, history } = props
    const { t } = useTranslation()
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    const handleOnSubmit = (formData: LoginFormData) => {
        const { email, password } = formData
        setIsSubmitting(true)
        return login(email, password)
            .then((success) => {
                if (success) {
                    const destinationPath = navigation.query().next
                    const newAccountAddScriptPage = destinationPath
                        ? destinationPath.indexOf('new_account') > 0 && destinationPath.indexOf('add_snippet') > 0
                        : false
                    if (destinationPath && !newAccountAddScriptPage) {
                        if (destinationPath[0] !== '/') {
                            history.push('/' + destinationPath)
                        } else {
                            history.push(destinationPath)
                        }
                    } else {
                        history.push('/')
                    }
                } else {
                    setErrorMessage(t('auth.login.invalidCredentials'))
                }
            })
            .catch(() => {
                setErrorMessage(t('auth.login.unknownError'))
            })
            .finally(() => {
                setIsSubmitting(false)
            })
    }

    return (
        <LoginPageContainer>
            <StyledFormWrapper
                formId="login-form"
                validationSchema={LoginFormSchema}
                initialValues={initialFormValues}
                onSubmit={handleOnSubmit}
                validateOnBlur={false}
            >
                <LoginHeader>{t('auth.login.title')}</LoginHeader>
                <LoginSubheader size={1}>{t('auth.login.subtitle')}</LoginSubheader>
                {errorMessage && (
                    <ValidationMessage id="login-message" className="validation-message-visible">
                        {errorMessage}
                    </ValidationMessage>
                )}
                <EmailInput
                    id="login-username"
                    type="email"
                    name="email"
                    block
                    label={t('auth.login.emailLabel')}
                    placeholder={t('auth.login.emailPlaceholder')}
                />
                <PasswordInput
                    id="login-password"
                    block
                    type="password"
                    name="password"
                    label={t('auth.login.passwordLabel')}
                    placeholder={t('auth.login.passwordLabel')}
                />
                <LoginButton disabled={isSubmitting} id="login-submit" size="large" type="submit" block>
                    {t('auth.login.signIn')}
                </LoginButton>
                <ForgotPasswordLinkWrapper size={1}>
                    <StyledATag href="/account/forgotPassword">{t('auth.login.forgotPassword')}</StyledATag>
                </ForgotPasswordLinkWrapper>
            </StyledFormWrapper>
        </LoginPageContainer>
    )
}

export default withNavigation(withFeatures(LoginPage))
